import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { sendToApi } from 'api/client'
import { BrandProfile, DeepPartial } from 'types'

interface Props {
  id?: number
}

export interface Data {
  brand_profile: BrandProfile
}

export interface Variables
  extends DeepPartial<Pick<BrandProfile, 'url' | 'profile' | 'keywords' | 'ideal_customer_profiles'>> {}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const updateBrandProfile = async ({
  workspaceId,
  id,
  ...variables
}: Variables & Props & { workspaceId: number }) => {
  const response = await sendToApi(`/w/${workspaceId}/brand_profiles/${id}`, {
    method: 'PUT',
    body: variables,
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useUpdateBrandProfile = ({ id }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, unknown, Variables>(
    'updateBrandProfile',
    async (variables) => {
      const result = await updateBrandProfile({ ...variables, workspaceId, id })
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandProfiles', 'detail', id])
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandProfile'])
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
      return result
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandProfiles', id])
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandProfile'])
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
